import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useCountry } from "../../context/CountryContext";
import { IMG } from "../PageHero";

const SLIDES = [
  { key: "fibre", bg: "bg-[#FFE4EA]", blob: "bg-[#F0326E]/15", img: IMG.fibre, eyebrow: "Uncapped fibre",
    title: (c) => <>Pure internet joy for every home in <span className="text-primary">{c}</span>.</>,
    sub: "Blazing-fast fibre from the best local networks. Free setup, month-to-month, cancel anytime.", cta: ["/internet/fibre", "Shop fibre"] },
  { key: "5g", bg: "bg-[#E4F2FC]", blob: "bg-[#0FB5A5]/20", img: IMG["5g"], eyebrow: "Fixed 5G & LTE",
    title: () => <>No trenching. No waiting. Just <span className="text-secondary">plug in &amp; go</span>.</>,
    sub: "High-speed wireless internet delivered to your door. Perfect where fibre hasn't reached yet.", cta: ["/internet/5g", "Explore 5G"] },
  { key: "domain", bg: "bg-[#DDF7EE]", blob: "bg-[#FFC23D]/35", img: IMG.domain, eyebrow: "Domains",
    title: () => <>Put your business on the map with a <span className="text-primary">.africa</span> name.</>,
    sub: "Search .africa, .com and your local country domain. One is free with any internet package.", cta: ["/domains", "Find a domain"] },
];

export const HeroSlider = () => {
  const { country } = useCountry();
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setI((i + 1) % SLIDES.length), 6500);
    return () => clearTimeout(t);
  }, [i, paused]);
  const s = SLIDES[i];
  return (
    <section className={`relative overflow-hidden transition-colors duration-700 ${s.bg}`} data-testid="hero-slider"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className={`blob absolute -left-28 -top-28 h-96 w-96 transition-colors duration-700 ${s.blob}`} />
      <div className="blob absolute -bottom-40 right-10 h-96 w-96 bg-white/40" style={{ animationDelay: "-6s" }} />
      <div className="relative mx-auto grid min-h-[520px] max-w-7xl items-center gap-6 px-4 pb-28 pt-10 sm:px-6 md:grid-cols-2 lg:px-8 lg:pb-32">
        <div key={s.key} className="fade-up">
          <p className="inline-flex rounded-full bg-white/80 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-primary shadow-sm">{s.eyebrow}</p>
          <h1 className="mt-5 text-4xl font-black leading-[1.02] sm:text-5xl lg:text-6xl" data-testid="hero-title">{s.title(country?.name || "Africa")}</h1>
          <p className="mt-5 max-w-lg text-base text-foreground/70 sm:text-lg">{s.sub}</p>
          <Link to={s.cta[0]} data-testid={`hero-cta-${s.key}`}
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-white shadow-[0_12px_28px_-10px_rgba(240,50,110,0.8)] transition-transform hover:-translate-y-0.5">
            {s.cta[1]} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div key={`img-${s.key}`} className="fade-in hidden md:block">
          <img src={s.img} alt="" className="float-y mx-auto w-full max-w-md rounded-[2.5rem] object-cover mix-blend-multiply" />
        </div>
      </div>
      <div className="absolute bottom-24 left-1/2 flex -translate-x-1/2 gap-2 lg:bottom-28" data-testid="hero-slider-dots">
        {SLIDES.map((x, k) => (
          <button key={x.key} onClick={() => setI(k)} data-testid={`hero-dot-${x.key}`} aria-label={x.eyebrow}
            className={`relative h-2 overflow-hidden rounded-full bg-foreground/15 transition-[width] duration-300 ${k === i ? "w-10" : "w-2"}`}>
            {k === i && !paused && <span className="absolute inset-0 origin-left rounded-full bg-primary" style={{ animation: "progress 6.5s linear" }} />}
            {k === i && paused && <span className="absolute inset-0 rounded-full bg-primary" />}
          </button>
        ))}
      </div>
    </section>
  );
};
